import { UpdateSocialInfoRequestModel } from "@payping/api";
import { Instagram, ShareOutlined, Telegram, Twitter } from "@payping/icons";
import { getUserSocials, updateSocials } from "@payping/redux/actions";
import { DashboardRoutes } from "@/router/constant";
import {
  Button,
  Col,
  Form,
  Image,
  Input,
  Page,
  Row,
  Space,
  Tooltip,
  Typography,
  useMessagedAsync,
  useMessagedAsyncFn,
} from "@payping/web-components";
import React, { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppDispatch, useAppSelector } from "@/utils";
import { FormLayout } from "@/components/DashUser/FormLayout";
import { StatusElement } from "@/components/DashUser/StatusElement";
import { URLS } from "@payping/utilities-shared/consts";

const SocialSection: React.FC = () => {
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const socials = useAppSelector((state) => state.user.socials);
  const socialStatus = useAppSelector((state) => state.user.checks?.socialsStatus);
  const [form] = Form.useForm<UpdateSocialInfoRequestModel>();
  const navigate = useNavigate();

  const { loading: loadingGet } = useMessagedAsync(
    async () => {
      await dispatch(getUserSocials());
    },
    [],
    { useProgress: true },
  );

  const [{ loading: loadingEdit }, handleEdit] = useMessagedAsyncFn(
    async (values: UpdateSocialInfoRequestModel) => {
      await dispatch(updateSocials(values));
      navigate(DashboardRoutes.profile.root.url);
    },
    [],
    undefined,
    { useProgress: true },
  );

  const loading = useMemo(() => loadingGet || loadingEdit, [loadingGet, loadingEdit]);

  useEffect(() => {
    form.setFieldsValue({
      instagram: socials?.instagram,
      telegram: socials?.telegram,
      twitter: socials?.twitter,
    });
  }, [form, socials]);

  return (
    <Page>
      <FormLayout
        title={
          <Row gutter={16}>
            <Col>
              <ShareOutlined width={58} />
            </Col>
            <Col>
              <Typography.Title level={4}>{t("profile.main.social")}</Typography.Title>
              <StatusElement completed={socialStatus} />
            </Col>
          </Row>
        }
        instructions={
          <ul>
            <li>{t("profile.social.description")}</li>
          </ul>
        }
      >
        <Form<UpdateSocialInfoRequestModel> onFinish={handleEdit} form={form} layout={"vertical"}>
          <Form.Item className="tw-flex tw-justify-center">
            <Image src={`${URLS.STORAGE_URL}/img/settings/settings-social-bg.png`} />
          </Form.Item>
          <Form.Item
            label={
              <Tooltip title={"Twitter"}>
                <Twitter width={"20px"} />
              </Tooltip>
            }
            name={"twitter"}
          >
            <Input skeleton={loading} direction={"ltr"} placeholder={"twitter"} />
          </Form.Item>
          <Form.Item
            label={
              <Tooltip title={"Telegram"}>
                <Telegram width={"20px"} />
              </Tooltip>
            }
            name={"telegram"}
          >
            <Input skeleton={loading} direction={"ltr"} placeholder={"telegram ID"} />
          </Form.Item>
          <Form.Item
            label={
              <Tooltip title={"Instagram"}>
                <Instagram width={"20px"} />
              </Tooltip>
            }
            name={"instagram"}
          >
            <Input skeleton={loading} direction={"ltr"} placeholder={"instagram"} />
          </Form.Item>
          <Space>
            <Button type={"primary"} loading={loading} htmlType={"submit"}>
              {t("save")}
            </Button>
            <Link to={DashboardRoutes.profile.root.url}>
              <Button variant={"outlined"} color="primary">
                {t("return")}
              </Button>
            </Link>
          </Space>
        </Form>
      </FormLayout>
    </Page>
  );
};

export default SocialSection;
